import { useEffect, useRef } from "react";
import { useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { getMyWorkspaces } from "api";
import { Constants, ConstantValues } from "common/constants";
import type { IWorkspace } from "models/IWorkspace";
import { RootState } from "store";
import { useChatStateReset } from "hooks/useChatStateReset";
import { setActiveWorkspace } from "../redux/activeWorkspaceSlice";
import { showToast } from "../redux/toastActions";
import { setWorkspaces } from "../redux/workspacesSlice";

const OPENING_WORKSPACE = "Opening workspace…";
const INVALID_LINK = "Invalid workspace link.";
const LOAD_FAILED = "Could not load workspaces.";
const NOT_FOUND_OR_NO_ACCESS = "Workspace not found or you don't have access.";
const NOT_A_MEMBER = "You don't belong to this workspace.";
const OPENED_ARCHIVED = "Opened archived workspace (read-only).";

export const ActivateWorkspace = (): JSX.Element => {
  const { id: urlWorkspaceId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const currentUser = useSelector((s: RootState) => s.currentUserSlice.value);
  const workspaceList = useSelector((s: RootState) => s.workspacesSlice.value);
  const resetChatState = useChatStateReset();

  const ranOnceRef = useRef(false);

  useEffect(() => {
    if (ranOnceRef.current) {
      return;
    }

    ranOnceRef.current = true;

    const openWorkspace = async () => {
      const DRAFT_KEY = ConstantValues.DRAFT_MESSAGE_WORKSPACE_ID;

      if (!urlWorkspaceId) {
        dispatch(showToast(INVALID_LINK, "error"));
        localStorage.removeItem(DRAFT_KEY);
        navigate("/workspaces", { replace: true });
        return;
      }

      let myWorkspaces: IWorkspace[] = workspaceList;
      if (!myWorkspaces || myWorkspaces.length === 0) {
        try {
          myWorkspaces = await getMyWorkspaces();
          dispatch(setWorkspaces(myWorkspaces));
        } catch {
          dispatch(showToast(LOAD_FAILED, "error"));
          localStorage.removeItem(DRAFT_KEY);
          navigate("/workspaces", { replace: true });
          return;
        }
      }

      const targetWorkspace = myWorkspaces.find((w) => w.id === urlWorkspaceId);
      if (!targetWorkspace) {
        dispatch(showToast(NOT_FOUND_OR_NO_ACCESS, "warning"));
        localStorage.removeItem(DRAFT_KEY);
        navigate("/workspaces", { replace: true });
        return;
      }

      const userId = currentUser.id;
      const roles = currentUser.roles ?? [];
      const isOwner =
        targetWorkspace.owners.some((o) => o.userId === userId) ?? false;
      const isMember =
        targetWorkspace.teamMembers?.some((m) => m.userId === userId) ?? false;
      const isPlatformAdmin = roles.includes(
        Constants.PLATFORM_ADMINS_GROUP_NAME_VALUE,
      );

      if (!(isOwner || isMember || isPlatformAdmin)) {
        dispatch(showToast(NOT_A_MEMBER, "error"));
        localStorage.removeItem(DRAFT_KEY);
        navigate("/workspaces", { replace: true });
        return;
      }

      resetChatState();
      dispatch(setActiveWorkspace(targetWorkspace));
      localStorage.setItem(DRAFT_KEY, targetWorkspace.id);
      if (targetWorkspace.archived) {
        dispatch(showToast(OPENED_ARCHIVED, "info"));
      }

      try {
        await queryClient.invalidateQueries(["conversations"]);
      } catch (e) {
        console.log(
          "Failed to refresh conversations after switching workspace",
          e,
        );
      }

      navigate("/", { replace: true });
    };

    openWorkspace();
  }, [
    urlWorkspaceId,
    workspaceList,
    currentUser,
    dispatch,
    navigate,
    queryClient,
    resetChatState,
  ]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        width: "100%",
      }}
    >
      <div>{OPENING_WORKSPACE}</div>
    </div>
  );
};
